import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from './api';

const CDNGenerator = () => {
  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [websiteUrl, setWebsiteUrl] = useState('');

  useEffect(() => {
    fetchScripts();
  }, []);

  const fetchScripts = async () => {
    try {
      const response = await api.get('/cdn/scripts');
      setScripts(response.data.data || []);
    } catch (error) {
      console.error('Error fetching scripts:', error);
      toast.error('Failed to load generated scripts');
    }
  };

  const handleInstantGenerate = async (e) => {
    if (e) e.preventDefault();
    setLoading(true);
    
    const getBackendUrl = () => {
      return 'https://api.stg.c-zentrix.com/api';
    };
    const backendUrl = getBackendUrl();
    
    let autoScriptName = 'tracker';
    if (websiteUrl) {
      try {
        const urlObj = new URL(websiteUrl.startsWith('http') ? websiteUrl : 'https://' + websiteUrl);
        autoScriptName = urlObj.hostname.replace(/www\./, '').replace(/\./g, '-');
      } catch (err) {
        autoScriptName = websiteUrl.replace(/[^a-zA-Z0-9-]/g, '-');
      }
    }
    
    // Add random suffix to avoid naming collision
    autoScriptName = `${autoScriptName}-${Math.floor(100 + Math.random() * 900)}`;

    const autoConfig = {
      apiUrl: backendUrl,
      debounceTime: 1000,
      trackClicks: true,
      trackFormSubmissions: true,
      customEvents: [],
      scriptName: autoScriptName
    };

    try {
      const response = await api.post('/cdn/generate', autoConfig);
      if (response.data.success) {
        toast.success('Tracking snippet generated!');
        setWebsiteUrl('');
        fetchScripts();
      }
    } catch (error) {
      toast.error('Failed to generate tracking script');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (fileName) => {
    if (!window.confirm(`Are you sure you want to delete script: ${fileName}?`)) return;
    try {
      await api.delete(`/cdn/script/${fileName}`);
      toast.success('Script deleted successfully');
      fetchScripts();
    } catch (error) {
      toast.error('Failed to delete script');
    }
  };

  const copyToClipboard = (text) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text)
        .then(() => toast.success('Copied code snippet to clipboard!'))
        .catch(() => fallbackCopyToClipboard(text));
    } else {
      fallbackCopyToClipboard(text);
    }
  };

  const fallbackCopyToClipboard = (text) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      document.execCommand('copy');
      toast.success('Copied code snippet to clipboard!');
    } catch (err) {
      toast.error('Could not copy text automatically. Please copy it manually.');
    }
    document.body.removeChild(textArea);
  };

  return (
    <div className="d-flex flex-column gap-4">
      {/* Header */}
      <div>
        <h2 className="h4 text-dark mb-1">Tracking Snippet Generator</h2>
        <p className="small text-muted mb-0">Deploy tracking script to capture all user clicks, links and forms automatically.</p>
      </div>

      {/* Website Input Form */}
      <div className="card-premium">
        <h3 className="h6 text-dark mb-3">Generate Script for Website</h3>
        <form onSubmit={handleInstantGenerate} className="d-flex flex-column flex-sm-row gap-3">
          <input
            type="text"
            value={websiteUrl}
            onChange={(e) => setWebsiteUrl(e.target.value)}
            placeholder="Enter website URL (e.g., https://stg.c-zentrix.com/)"
            className="form-control"
          />
          <button
            type="submit"
            disabled={loading}
            className="btn btn-primary px-4 text-nowrap"
          >
            {loading ? 'Generating...' : 'Generate Script'}
          </button>
        </form>
        <p className="small text-muted mt-2 mb-0" style={{ fontSize: '11px' }}>
          Generating a script automatically enables click tracking, link click tracking, and form tracking.
        </p>
      </div>

      {/* Existing Scripts Grid */}
      <div className="card-premium">
        <h3 className="h6 text-dark mb-4">Active Deployments</h3>
        
        {scripts.length === 0 ? (
          <div className="text-center py-5 border border-dashed rounded-3">
            <p className="text-muted mb-1">No scripts generated yet.</p>
            <p className="small text-muted mb-0">Enter your website URL above to generate a tracker snippet.</p>
          </div>
        ) : (
          <div className="d-flex flex-column gap-4">
            {scripts.map((script, index) => {
              const fullCdnUrl = script.path;
              const embedTag = `<script src="${fullCdnUrl}"></script>`;
              const inlineTag = `<script>${script.content || ''}</script>`;

              return (
                <div key={index} className="p-4 bg-light border rounded-3 d-flex flex-column gap-3">
                  <div className="d-flex justify-content-between align-items-start gap-3">
                    <div>
                      <h4 className="h6 mb-1 text-dark font-weight-bold">{script.fileName}</h4>
                      <p className="small text-muted mb-0" style={{ fontSize: '11px' }}>
                        Created: {new Date(script.createdAt).toLocaleString()} • Size: {(script.size / 1024).toFixed(2)} KB
                      </p>
                    </div>
                    <button
                      onClick={() => handleDelete(script.fileName)}
                      className="btn btn-outline-danger btn-sm"
                    >
                      Delete
                    </button>
                  </div>

                  {/* Option A: CDN Script Tag */}
                  <div className="d-flex flex-column gap-2">
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="small text-muted text-uppercase font-weight-bold" style={{ fontSize: '10px' }}>Option A: CDN Script Tag</span>
                      <button
                        onClick={() => copyToClipboard(embedTag)}
                        className="btn btn-link p-0 text-decoration-none small"
                        style={{ fontSize: '12px' }}
                      >
                        Copy CDN Tag
                      </button>
                    </div>
                    <code className="small text-primary bg-white p-2.5 rounded border block text-truncate font-monospace">
                      {embedTag}
                    </code>
                  </div>

                  {/* Option B: Direct Inline JavaScript */}
                  <div className="d-flex flex-column gap-2">
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="small text-muted text-uppercase font-weight-bold" style={{ fontSize: '10px' }}>Option B: Direct Inline JavaScript (Immune to CDN Blocks)</span>
                      <button
                        onClick={() => copyToClipboard(inlineTag)}
                        className="btn btn-link p-0 text-decoration-none small text-success"
                        style={{ fontSize: '12px' }}
                      >
                        Copy Inline Code
                      </button>
                    </div>
                    <pre className="small text-dark bg-white p-3 rounded border font-monospace mb-0" style={{ fontSize: '11px', maxHeight: '100px', overflowY: 'auto', whiteSpace: 'pre-wrap' }}>
                      {inlineTag}
                    </pre>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default CDNGenerator;
